// Shared per-session detail panels used in both the unified CallDetail
// view (AiSessionTabContent) and the legacy SessionDetail view.

import React, { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Code,
  Divider,
  Grid,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconSparkles } from '@tabler/icons-react';

import { useAuth } from '../../../../shared/contexts/AuthContext';
import {
  summarizeAiAgentSession,
  isWxccAiOpsBackendConfigured,
} from '../../api_scripts/wxccAiOpsClient';
import { ErrorAlert, Metric, fmtTs } from './utils';

export function AuditPanel({ loading, error, data }) {
  if (loading) return null;
  if (error)
    return (
      <Alert color="yellow" icon={<IconAlertTriangle size={16} />} title="Audit unavailable">
        <Text size="xs">{error}</Text>
      </Alert>
    );
  if (!data?.analysis) return null;

  const { analysis } = data;
  const routing = analysis.routing || {};
  const ungrounded = analysis.ungrounded || {};
  const latency = analysis.observedLatency || {};

  return (
    <Paper withBorder p="sm" radius="md">
      <Title order={5} mb="xs">
        Audit summary — this session
      </Title>
      <Grid gutter="md">
        <Metric label="Bot turns" value={analysis.botTurns ?? '?'} />
        <Metric
          label="Ungrounded (heuristic)"
          value={`${ungrounded.count ?? '?'} (${ungrounded.pct ?? '?'}%)`}
          hint={ungrounded.definition}
        />
        <Metric label="Errors" value={analysis.observedErrors ?? '?'} />
        <Metric label="Max latency" value={latency.maximumMs != null ? `${latency.maximumMs} ms` : 'n/a'} />
      </Grid>
      {routing.classifierCalls != null && (
        <Paper mt="sm" p="sm" bg="gray.0" radius="md">
          <Text size="xs" c="dimmed" mb={2}>
            Router behaviour
          </Text>
          <Text size="sm">
            <Code>classifierCalls</Code> = {routing.classifierCalls} · <Code>smallTalkRate</Code> ={' '}
            <strong>{routing.smallTalkRatePct}%</strong> · <Code>agentSelectedRate</Code> ={' '}
            <strong>{routing.agentSelectedRatePct}%</strong>
          </Text>
          {routing.note && (
            <Text size="xs" c="dimmed" mt={4}>
              {routing.note}
            </Text>
          )}
        </Paper>
      )}
    </Paper>
  );
}

export function MessageList({ loading, error, data }) {
  if (loading) return <Loader size="sm" />;
  if (error) return <ErrorAlert message={error} />;
  const messages = data?.messages || [];
  const total = data?.totalMessages ?? messages.length;
  if (messages.length === 0)
    return (
      <Paper withBorder p="md" radius="md">
        <Text size="sm" c="dimmed">
          No messages.
        </Text>
      </Paper>
    );
  return (
    <>
      <Text size="xs" c="dimmed" mb="xs">
        {messages.length} of {total} messages · <Code>text</Code> is E2E-encrypted; metadata below is
        not.
      </Text>
      <Stack gap="xs">
        {messages.map((m, i) => (
          <MessageRow key={m.id || i} m={m} />
        ))}
      </Stack>
    </>
  );
}

function MessageRow({ m }) {
  const role = m.userType || 'unknown';
  const meta = m.metadata || {};
  const color =
    role === 'human' ? 'blue' : role === 'bot' ? 'teal' : role === 'custom_data' ? 'gray' : 'yellow';
  return (
    <Paper withBorder p="xs" radius="sm" style={{ borderLeft: `3px solid var(--mantine-color-${color}-5)` }}>
      <Group justify="space-between" mb={4}>
        <Badge size="xs" variant="light" color={color}>
          {role}
        </Badge>
        <Text size="xs" c="dimmed">
          {fmtTs(m.createdAt, true)}
          {meta.latency > 0 && ` · ${meta.latency}ms`}
        </Text>
      </Group>
      {meta.parameters && Object.keys(meta.parameters).length > 0 && (
        <Paper bg="gray.0" p="xs" radius="sm" mt={4}>
          <Text size="xs" c="dimmed" mb={2}>
            parameters
          </Text>
          <Code block style={{ fontSize: 11 }}>
            {JSON.stringify(meta.parameters, null, 2)}
          </Code>
        </Paper>
      )}
      {Array.isArray(meta.tools) && meta.tools.length > 0 && (
        <details style={{ marginTop: 6 }}>
          <summary style={{ fontSize: 11, cursor: 'pointer', color: '#666' }}>
            Tool invocations ({meta.tools.length})
          </summary>
          <Code block style={{ fontSize: 10, marginTop: 4 }}>
            {JSON.stringify(meta.tools, null, 2)}
          </Code>
        </details>
      )}
    </Paper>
  );
}

export function TimelinePanel({ interactionId, loading, error, data }) {
  if (!interactionId)
    return (
      <Paper withBorder p="md" radius="md">
        <Text size="sm" c="dimmed">
          No <Code>interactionId</Code> on this session.
        </Text>
      </Paper>
    );
  if (loading) return <Loader size="sm" />;
  if (error)
    return (
      <Alert color="yellow" icon={<IconAlertTriangle size={16} />} title="Contact timeline unavailable">
        <Text size="xs">{error}</Text>
      </Alert>
    );
  const events = data?.events || [];
  return (
    <>
      <Text size="xs" c="dimmed" mb="xs">
        {events.length} events
      </Text>
      <Stack gap="xs">
        {events.map((e, i) => (
          <Paper key={i} withBorder p="xs" radius="sm" style={{ borderLeft: '3px solid var(--mantine-color-grape-5)' }}>
            <Group justify="space-between" mb={4}>
              <Badge size="xs" variant="light" color="grape">
                {e.type || e.eventType || 'event'}
              </Badge>
              <Text size="xs" c="dimmed">
                {fmtTs(e.timestamp || e.time || e.createdAt || e.at, true)}
              </Text>
            </Group>
            <details>
              <summary style={{ fontSize: 11, cursor: 'pointer', color: '#666' }}>Details</summary>
              <Code block style={{ fontSize: 10, marginTop: 4 }}>
                {JSON.stringify(e, null, 2)}
              </Code>
            </details>
          </Paper>
        ))}
        {events.length === 0 && (
          <Paper withBorder p="md" radius="md">
            <Text size="xs" c="dimmed" mb="xs">
              No events array in response — raw payload:
            </Text>
            <Code block style={{ fontSize: 10 }}>
              {JSON.stringify(data, null, 2).slice(0, 2000)}
            </Code>
          </Paper>
        )}
      </Stack>
    </>
  );
}

export function SessionHeader({ loading, error, session }) {
  if (loading) return <Loader size="sm" />;
  if (error) return <ErrorAlert message={error} />;
  if (!session) return null;
  const items = [
    ['State', session.state],
    ['Test?', session.isTest ? 'true' : 'false'],
    ['Start', fmtTs(session.createdAt)],
    ['End', fmtTs(session.updatedAt)],
  ];
  return (
    <Paper withBorder p="sm" radius="md">
      <Grid gutter="md">
        {items.map(([label, val]) => (
          <Grid.Col key={label} span={{ base: 6, md: 3 }}>
            <Text size="xs" c="dimmed">
              {label}
            </Text>
            <Text size="sm" fw={500}>
              {val || <em style={{ color: '#999' }}>n/a</em>}
            </Text>
          </Grid.Col>
        ))}
      </Grid>
    </Paper>
  );
}

export function AiSummarySection({ agentId, sessionId }) {
  const { getApiToken } = useAuth();
  const configured = isWxccAiOpsBackendConfigured();
  const [state, setState] = useState({ loading: false, summary: null, error: null, sourcesUsed: null, durationMs: null });

  async function generate() {
    setState({ loading: true, summary: null, error: null, sourcesUsed: null, durationMs: null });
    try {
      const res = await summarizeAiAgentSession({ agentId, sessionId }, getApiToken);
      setState({ loading: false, summary: res.summary, error: null, sourcesUsed: res.sourcesUsed, durationMs: res.durationMs });
    } catch (e) {
      setState({ loading: false, summary: null, error: e.message, sourcesUsed: null, durationMs: null });
    }
  }

  return (
    <Paper withBorder p="sm" radius="md">
      <Group justify="space-between" mb="xs">
        <Title order={5}>AI Summary (Bedrock)</Title>
        <Button
          size="xs"
          leftSection={<IconSparkles size={14} />}
          loading={state.loading}
          disabled={!configured}
          onClick={generate}
        >
          Generate summary
        </Button>
      </Group>
      {!configured && (
        <Text size="xs" c="dimmed">
          REACT_APP_CCUTILS_API_BASE_URL not set — Lambda backend unavailable in this build. Set it to enable
          Bedrock summarization.
        </Text>
      )}
      {state.error && <ErrorAlert message={state.error} />}
      {state.summary && (
        <>
          <Divider my="xs" />
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {state.summary}
          </Text>
          {state.durationMs != null && (
            <Text size="xs" c="dimmed" mt="xs">
              {(state.durationMs / 1000).toFixed(1)}s
            </Text>
          )}
        </>
      )}
    </Paper>
  );
}
